import React, { useCallback, useState } from 'react'
import { Accept, useDropzone } from 'react-dropzone'
import CopaDropzonePanel from './CopaDropzonePanel'
import CopaFileRow from './CopaFileRow'

interface SingleFileDropzoneForCopaProps {
  onFileChange: (file: File | null) => void
  file: File | null
  accept: Accept
  title: string
  showDropzone?: boolean
  description?: string
  hint?: string
  rejectionHint?: string
}

const SingleFileDropzoneForCopa: React.FC<SingleFileDropzoneForCopaProps> = ({
  onFileChange,
  file,
  accept,
  title,
  showDropzone = false,
  description,
  hint,
  rejectionHint,
}) => {
  const [error, setError] = useState<string | null>(null)

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      setError(null)
      if (acceptedFiles.length > 0) {
        onFileChange(acceptedFiles[0]) // Take only the first file
      }
    },
    [onFileChange],
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    maxFiles: 1,
    multiple: false,
    onDropRejected: (fileRejections) => {
      const errorMessage = fileRejections[0]?.errors[0]?.message || 'File type not allowed'
      setError(rejectionHint ? `${errorMessage}. ${rejectionHint}` : errorMessage)
      setTimeout(() => setError(null), 3000) // Clear error after 3 seconds
    },
  })

  const removeFile = useCallback(() => {
    onFileChange(null)
  }, [onFileChange])

  if (!file && !showDropzone) return null

  return (
    <div className="space-y-4">
      {showDropzone && !file && (
        <CopaDropzonePanel
          rootProps={getRootProps()}
          inputProps={getInputProps()}
          isDragActive={isDragActive}
          title={title}
          description={description}
          hint={hint}
          error={error}
          onCancel={removeFile}
          className="min-h-[200px]"
        />
      )}
      {file && <CopaFileRow file={file} onRemove={removeFile} showCheck />}
    </div>
  )
}

export default SingleFileDropzoneForCopa
